/**
 * Live data for the main dashboard widgets (components/workspace/LiveWidgets).
 *
 * One entry per data source, fetched on demand, cached for a minute and shared
 * by every widget that reads it (e.g. four widgets read /people/dashboard, one
 * request). Rows are normalised to camelCase here, never in the widgets.
 *
 * Failures are kept, not hidden: a widget shows "You don't have access" for a
 * 403 and the service's own message otherwise — never made-up numbers.
 */
import { create } from 'zustand';
import { apiClient } from '../lib/api';

const TTL_MS = 60_000;

const list = (raw: any): any[] => {
    const d = raw?.data ?? raw;
    return Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : [];
};
const one = (raw: any) => raw?.data ?? raw;
const num = (v: unknown) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);

export interface AssetRow { id: string; code: string; name: string; status: string; category: string | null; assignedTo: string | null; warrantyEnd: string | null }
export interface MaintenanceRow { id: string; asset: string | null; type: string | null; status: string | null; scheduledFor: string | null; description: string | null }
export interface ProjectRow { id: string; name: string; status: string; endDate: string | null }
export interface TaskRow { id: string; title: string; status: string; priority: string | null; dueDate: string | null; assigneeId: string | null }
export interface NotificationRow { id: string; title: string; message: string; type: string; isRead: boolean; createdAt: string; link: string | null }
export interface UserRow { id: string; name: string; email: string; status: string; roles: string[] }
export interface MoneyRow { id: string; number: string | null; party: string | null; amount: number; status: string | null; date: string | null; dueDate: string | null }

/** url + how to turn the response into what the widgets read. */
const SOURCES = {
    peopleDashboard: { url: '/people/dashboard', map: one },
    attendanceSummary: { url: '/people/attendance/admin/summary', map: one },
    payrollStats: { url: '/people/payroll/stats', map: one },
    assets: {
        url: '/assets/assets?limit=500',
        map: (raw: any): AssetRow[] => list(raw).map((a) => ({
            id: a.id, code: a.asset_code ?? a.assetCode ?? '', name: a.name, status: a.status ?? 'Unknown', category: a.category ?? null,
            assignedTo: a.assigned_to_name ?? a.assignedToName ?? null, warrantyEnd: a.warranty_end_date ?? a.warranty_until ?? null,
        })),
    },
    maintenance: {
        url: '/assets/maintenance?limit=50',
        map: (raw: any): MaintenanceRow[] => list(raw).map((m) => ({
            id: m.id, asset: m.asset_name ?? m.asset_code ?? m.assetName ?? null, type: m.maintenance_type ?? m.type ?? null,
            status: m.status ?? null, scheduledFor: m.scheduled_date ?? m.scheduled_for ?? m.due_date ?? m.start_date ?? null,
            description: m.description ?? m.title ?? null,
        })),
    },
    supportQueues: {
        url: '/support/tickets/queues-summary',
        map: (raw: any) => list(raw).map((q) => ({ key: q.key, label: q.label, count: num(q.count) })),
    },
    supportTickets: {
        url: '/support/tickets?limit=100',
        map: (raw: any) => list(raw).map((t) => ({
            id: t.id, number: t.ticket_number ?? t.number ?? null, subject: t.subject ?? t.title ?? '',
            status: t.status ?? null, priority: t.priority ?? null, slaBreached: Boolean(t.sla?.breached ?? t.sla_breached ?? t.slaBreached),
            dueAt: t.resolution_due_at ?? t.due_at ?? t.sla_due_at ?? null,
        })),
    },
    projects: {
        url: '/work/projects?limit=100',
        map: (raw: any): ProjectRow[] => list(raw).map((p) => ({ id: p.id, name: p.name, status: p.status ?? 'unknown', endDate: p.end_date ?? null })),
    },
    tasks: {
        url: '/work/tasks?limit=200',
        map: (raw: any): TaskRow[] => list(raw).map((t) => ({
            id: t.id, title: t.title, status: t.status ?? 'unknown', priority: t.priority ?? null, dueDate: t.due_date ?? null, assigneeId: t.assignee_id ?? null,
        })),
    },
    timesheets: {
        url: '/work/timesheets?limit=200',
        map: (raw: any) => list(raw).map((t) => ({ id: t.id, status: t.status ?? null, hours: num(t.total_hours ?? t.hours) })),
    },
    notifications: {
        url: '/auth/notifications',
        map: (raw: any): NotificationRow[] => list(raw).map((n) => ({
            id: n.id, title: n.title, message: n.message, type: n.type ?? 'info', isRead: Boolean(n.is_read),
            createdAt: n.created_at, link: n.metadata?.link ?? null,
        })),
    },
    users: {
        url: '/people/rbac/users',
        map: (raw: any): UserRow[] => list(raw).map((u) => ({
            id: u.id, name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email, email: u.email,
            status: u.status ?? 'unknown', roles: Array.isArray(u.roles) ? u.roles.map((r: any) => r?.name ?? r) : [],
        })),
    },
    invoices: {
        url: '/money/invoices?limit=200',
        map: (raw: any): MoneyRow[] => list(raw).map((i) => ({
            id: i.id, number: i.invoice_number ?? i.number ?? null, party: i.customer_name ?? i.client_name ?? null,
            amount: num(i.total_amount ?? i.total ?? i.amount), status: i.status ?? null, date: i.issue_date ?? i.created_at ?? null, dueDate: i.due_date ?? null,
        })),
    },
    expenses: {
        url: '/money/expenses?limit=200',
        map: (raw: any): MoneyRow[] => list(raw).map((e) => ({
            id: e.id, number: null, party: e.category ?? e.vendor ?? null, amount: num(e.amount ?? e.total_amount),
            status: e.status ?? null, date: e.expense_date ?? e.date ?? e.created_at ?? null, dueDate: null,
        })),
    },
    campaigns: {
        url: '/growth/campaigns',
        map: (raw: any) => list(raw).map((c) => ({ id: c.id, name: c.name, status: c.status ?? null })),
    },
    leads: {
        url: '/growth/leads',
        map: (raw: any) => list(raw).map((l) => ({ id: l.id, name: l.name ?? l.company ?? '', status: l.status ?? null, createdAt: l.created_at ?? null })),
    },
} as const;

export type SourceKey = keyof typeof SOURCES;
type SourceData<K extends SourceKey> = ReturnType<(typeof SOURCES)[K]['map']>;

export interface SourceState<T = any> {
    data: T | null;
    loading: boolean;
    /** Human message when the last load failed. */
    error: string | null;
    /** HTTP status of the last failure (403 = no access). */
    status: number | null;
    /** The service's own technical message, for the tooltip / bug reports. */
    detail?: string | null;
    at: number;
}

const EMPTY: SourceState = { data: null, loading: false, error: null, status: null, at: 0 };

interface WorkspaceDataState {
    sources: Partial<Record<SourceKey, SourceState>>;
    load: (key: SourceKey, force?: boolean) => Promise<void>;
    refreshAll: () => void;
}

const failure = (e: any): { error: string; status: number | null; detail: string | null } => {
    const status: number | null = e?.response?.status ?? null;
    const detail: string | null = e?.response?.data?.message || e?.response?.data?.error || e?.message || null;
    if (status === 403) return { status, detail, error: "You don't have access to this." };
    if (status === 404) return { status, detail, error: 'This service does not provide this data yet.' };
    if (status && status >= 500) return { status, detail, error: 'The service had an internal error. Please try again later or contact support.' };
    return { status, detail, error: 'The service could not be reached.' };
};

export const useWorkspaceDataStore = create<WorkspaceDataState>((set, get) => ({
    sources: {},

    load: async (key, force = false) => {
        const cur = get().sources[key] ?? EMPTY;
        if (cur.loading || (!force && cur.at && Date.now() - cur.at < TTL_MS)) return;
        set((s) => ({ sources: { ...s.sources, [key]: { ...cur, loading: true } } }));
        try {
            const res = await apiClient.get(SOURCES[key].url);
            const data = (SOURCES[key].map as (raw: any) => unknown)(res.data);
            set((s) => ({ sources: { ...s.sources, [key]: { data, loading: false, error: null, status: null, at: Date.now() } } }));
        } catch (e) {
            set((s) => ({ sources: { ...s.sources, [key]: { data: null, loading: false, ...failure(e), at: Date.now() } } }));
        }
    },

    refreshAll: () => {
        (Object.keys(get().sources) as SourceKey[]).forEach((k) => get().load(k, true));
    },
}));

/** Selector helper: the typed state of one source. */
export function selectSource<K extends SourceKey>(key: K) {
    return (s: WorkspaceDataState) => (s.sources[key] ?? EMPTY) as SourceState<SourceData<K>>;
}
